/** Cosmetic flow coordinates. The shared river graph remains gameplay authority. */
import type {RiverPoint} from '@hyperlight/shared';

export interface FlowClock { s:Float64Array; seconds:Float64Array }

/** Integrate travel time once per river. Chunk boundaries share this clock;
 * multiplying arclength by a changing speed would stretch and tear the phase. */
export function buildFlowClock(points:readonly RiverPoint[]):FlowClock {
  const s=Float64Array.from(points,p=>p.s),seconds=new Float64Array(points.length);
  for(let i=1;i<points.length;i++){
    const a=Math.max(.1,points[i-1].speed),b=Math.max(.1,points[i].speed);
    seconds[i]=seconds[i-1]+Math.max(0,s[i]-s[i-1])*.5*(1/a+1/b);
  }
  return {s,seconds};
}

/** Continuous interpolation of the precomputed clock, including shared rows. */
export function flowSeconds(clock:FlowClock,s:number):number {
  if(!clock.s.length)return 0;
  let lo=0,hi=clock.s.length-1;
  if(s<=clock.s[0])return clock.seconds[0];
  if(s>=clock.s[hi])return clock.seconds[hi];
  while(hi-lo>1){const mid=(lo+hi)>>>1;if(clock.s[mid]<=s)lo=mid;else hi=mid;}
  const t=(s-clock.s[lo])/Math.max(.0001,clock.s[hi]-clock.s[lo]);
  return clock.seconds[lo]+(clock.seconds[hi]-clock.seconds[lo])*t;
}

/** Fragment-only surface detail: no new texture, displacement or fluid state.
 * All moving frequencies complete whole cycles in the 600 s water clock. */
export const WATER_CHARACTER_GLSL=/* glsl */ `
float waterEnergy(float grade, float speed) {
  return smoothstep(0.015,0.20,max(0.0,grade)) * mix(0.6,1.0,smoothstep(0.9,3.4,speed));
}
// Quiet surfaces retain broad reflections. Steeper moving water breaks them
// with small flow-aligned ripples, with no ocean-scale crossing swells.
vec2 riverRipples(float travel, float across, float time, float energy, float detail) {
  // Broad triangular surface planes break a reflection into short angular
  // shapes. Only normals change; there is no painted noise over the water.
  vec2 p=vec2(across*.55,travel*.8),cell=floor(p),q=fract(p);
  float triangle=step(1.0,q.x+q.y);
  vec2 centre=cell+mix(vec2(1.0/3.0),vec2(2.0/3.0),triangle);
  across=centre.x/.55;
  travel=centre.y/.8;
  float phase=(travel-time)*6.28318530718;
  float a=phase*(132.0/600.0)+across*0.73;
  float b=phase*(69.0/600.0)-across*1.13;
  float envelope=0.72+0.28*sin(across*0.31+travel*0.09);
  return vec2(cos(a)+0.45*cos(b),0.38*sin(b)-0.22*sin(a))
    *mix(0.012,0.048,energy)*envelope*detail;
}
// A continuous pattern breaks the waterline into patches instead of a white
// contour. Derivative filtering removes fine bands before they alias at range.
float waterBreakup(vec2 p, float phase) {
  float wide=sin(p.x*0.41+p.y*0.27+phase);
  float medium=sin(p.x*1.17-p.y*0.83-phase*2.0);
  float resolved=1.0-smoothstep(0.35,2.5,max(fwidth(p.x),fwidth(p.y)));
  return mix(0.32,smoothstep(-0.18,0.7,wide*.62+medium*.38),resolved);
}
`;
